import { useCallback, useEffect, useMemo, useState } from 'react'
import { BarChart3, Gavel, LayoutGrid, ShieldAlert, ShieldCheck, Tags } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { ErrorState, EmptyState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import type { CampaignRow } from './adsApi'
import {
  fetchAdStats,
  fetchChannelRevenue,
  fetchBannedCategories,
  type AdStats,
  type ChannelRevenue,
  type BannedCategory,
} from './adsExtraApi'
import { fetchCampaignsList, fetchAuction, fetchFraud, ADSD_STR as S, type AuctionSettings, type FraudRow } from './adsDetailApi'

/* ── shared load hook ────────────────────────────────────────────── */
function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  return { data, err, reload: useCallback(() => setTick((t) => t + 1), []) }
}

function Loading({ n = 6 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <p className="text-xs text-tertiary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-primary tabular">{value}</p>
    </div>
  )
}

/* ── A20.12 — Reklama joylashuvlari (inventar) ──────────────────── */
export function AdPlacementsOverviewScreen() {
  const { data, err, reload } = useLoad<AdStats>(fetchAdStats)
  const slots = [
    { key: 'feed', title: S.feed, desc: S.feedDesc, icon: <LayoutGrid size={18} /> },
    { key: 'banner', title: S.banner, desc: S.bannerDesc, icon: <LayoutGrid size={18} /> },
    { key: 'interstitial', title: S.interstitial, desc: S.interstitialDesc, icon: <LayoutGrid size={18} /> },
  ]
  return (
    <>
      <PageHeader title={S.placementsOverview} screenId="A20.12" description={S.placementsOverviewDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          <div className="grid gap-2.5 sm:grid-cols-3">
            {slots.map((sl) => (
              <div key={sl.key} className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="mb-2 flex items-center gap-2 text-accent">{sl.icon}</div>
                <p className="text-sm font-medium text-primary">{sl.title}</p>
                <p className="mt-1 text-xs text-tertiary">{sl.desc}</p>
                <code className="mt-2 inline-block rounded bg-surface-3 px-1.5 py-0.5 text-xs text-secondary">
                  {sl.key}
                </code>
              </div>
            ))}
          </div>
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <Loading n={2} />
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-3">
              <StatCard label={S.activeCampaigns} value={data.active_campaigns.toLocaleString()} />
              <StatCard label={S.impressions} value={data.impressions.toLocaleString()} />
              <StatCard label={S.clicks} value={data.clicks.toLocaleString()} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A20.13 — Narxlash (CPM) ────────────────────────────────────── */
export function PricingCpmScreen() {
  const { data, err, reload } = useLoad<CampaignRow[]>(fetchCampaignsList)
  const agg = useMemo(() => {
    if (!data || data.length === 0) return null
    const cpms = data.map((c) => c.cpm).filter((v) => v > 0)
    if (cpms.length === 0) return { min: 0, avg: 0, max: 0 }
    const sum = cpms.reduce((a, b) => a + b, 0)
    return { min: Math.min(...cpms), avg: Math.round(sum / cpms.length), max: Math.max(...cpms) }
  }, [data])

  return (
    <>
      <PageHeader title={S.pricing} screenId="A20.13" description={S.pricingDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : (
          <div className="mx-auto max-w-4xl space-y-4">
            <div className="grid gap-2.5 sm:grid-cols-3">
              <StatCard label={S.minCpm} value={(agg?.min ?? 0).toLocaleString()} />
              <StatCard label={S.avgCpm} value={(agg?.avg ?? 0).toLocaleString()} />
              <StatCard label={S.maxCpm} value={(agg?.max ?? 0).toLocaleString()} />
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default">
              <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                {S.perCampaign}
              </p>
              {data.length === 0 ? (
                <EmptyState title={S.empty} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-2 text-xs text-tertiary">
                      <tr>
                        <th className="px-4 py-2 font-medium">{S.campaign}</th>
                        <th className="px-4 py-2 font-medium">{S.advertiser}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.cpm}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.impressions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((c) => (
                        <tr key={c.id} className="border-t border-border-subtle">
                          <td className="px-4 py-2 text-primary">{c.title}</td>
                          <td className="px-4 py-2 text-secondary">{c.advertiser}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">{c.cpm.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">
                            {c.impressions.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.14 — Auksion sozlamalari (dormant → read-only) ─────────── */
export function AuctionSettingsScreen() {
  const { data, err, reload } = useLoad<AuctionSettings>(fetchAuction)
  return (
    <>
      <PageHeader title={S.auction} screenId="A20.14" description={S.auctionDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading n={4} />
        ) : (
          <div className="mx-auto max-w-xl space-y-3">
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300">
              <Gavel size={14} className="mt-0.5 shrink-0" />
              <span>{S.auctionDormant}</span>
            </div>
            <dl className="divide-y divide-border-subtle rounded-lg border border-border-default bg-surface-2 px-4">
              <RowKV label={S.model} value={data.model} />
              <RowKV label={S.floorCpm} value={data.floor_cpm.toLocaleString()} />
              <RowKV label={S.reservePct} value={`${data.reserve_pct}%`} />
              <RowKV label={S.enabled} value={data.enabled ? S.yes : S.no} />
            </dl>
          </div>
        )}
      </div>
    </>
  )
}

function RowKV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-xs text-tertiary">{label}</span>
      <span className="text-sm text-primary">{value}</span>
    </div>
  )
}

/* ── A20.17 — Firibgar reklamalar (dormant DataTable, fail-soft) ── */
export function FraudAdsScreen() {
  const columns = useMemo<AdminColumn<FraudRow>[]>(
    () => [
      { key: 'campaign', header: S.campaign, type: 'text' },
      { key: 'advertiser', header: S.advertiser, type: 'text' },
      { key: 'signal', header: S.fraudSignal, type: 'text' },
      { key: 'score', header: S.fraudScore, type: 'number' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'detected_at', header: S.detectedAt, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchFraud, [])
  return (
    <>
      <PageHeader title={S.fraud} screenId="A20.17" description={S.fraudDormant} />
      <DataTable<FraudRow>
        screenId="A20.17"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/* ── A20.18 — Brand safety (real: banned-categories block-list) ─── */
export function BrandSafetyScreen() {
  const { data, err, reload } = useLoad<BannedCategory[]>(fetchBannedCategories)
  return (
    <>
      <PageHeader title={S.brandSafety} screenId="A20.18" description={S.brandSafetyDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : data.length === 0 ? (
          <EmptyState icon={<ShieldCheck size={28} strokeWidth={1.5} />} title={S.brandSafetyEmpty} />
        ) : (
          <div className="mx-auto max-w-2xl overflow-hidden rounded-lg border border-border-default">
            {data.map((c, i) => (
              <div
                key={c.id}
                className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
              >
                <Tags size={15} className="shrink-0 text-tertiary" />
                <div className="min-w-0">
                  <p className="truncate text-sm text-primary">{c.category}</p>
                  {c.note && <p className="truncate text-xs text-tertiary">{c.note}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.19 — Reklama hisobotlari (real: stats + channel-revenue) ─ */
export function AdsReportsScreen() {
  const stats = useLoad<AdStats>(fetchAdStats)
  const rev = useLoad<ChannelRevenue[]>(fetchChannelRevenue)
  return (
    <>
      <PageHeader title={S.reports} screenId="A20.19" description={S.reportsDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {stats.err ? (
            <ErrorState code={stats.err.code} onRetry={stats.reload} />
          ) : !stats.data ? (
            <Loading n={2} />
          ) : (
            <>
              <div className="grid gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
                <StatCard label={S.advertisersCnt} value={stats.data.advertisers.toLocaleString()} />
                <StatCard label={S.activeCampaigns} value={stats.data.active_campaigns.toLocaleString()} />
                <StatCard label={S.totalSpend} value={stats.data.total_spend.toLocaleString()} />
                <StatCard label={S.impressions} value={stats.data.impressions.toLocaleString()} />
                <StatCard label={S.clicks} value={stats.data.clicks.toLocaleString()} />
                <StatCard label={S.ctr} value={`${stats.data.ctr.toFixed(2)}%`} />
              </div>
              {stats.data.top_campaigns && stats.data.top_campaigns.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border-default">
                  <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                    {S.topCampaigns}
                  </p>
                  {stats.data.top_campaigns.map((t, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between px-4 py-2 text-sm ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                    >
                      <span className="truncate text-primary">{t.title}</span>
                      <span className="shrink-0 tabular text-secondary">{t.spent.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="overflow-hidden rounded-lg border border-border-default">
            <p className="flex items-center gap-2 border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
              <BarChart3 size={13} />
              {S.channelRevenue}
            </p>
            {rev.err ? (
              <ErrorState code={rev.err.code} onRetry={rev.reload} />
            ) : !rev.data ? (
              <Loading n={3} />
            ) : rev.data.length === 0 ? (
              <EmptyState title={S.empty} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-surface-2 text-xs text-tertiary">
                    <tr>
                      <th className="px-4 py-2 font-medium">{S.channel}</th>
                      <th className="px-4 py-2 text-right font-medium">{S.impressions}</th>
                      <th className="px-4 py-2 text-right font-medium">{S.revenue}</th>
                      <th className="px-4 py-2 text-right font-medium">{S.updated}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rev.data.map((c, i) => (
                      <tr key={i} className="border-t border-border-subtle">
                        <td className="px-4 py-2 font-mono text-primary">{c.channel_ref}</td>
                        <td className="px-4 py-2 text-right tabular text-secondary">{c.impressions.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right tabular text-secondary">{c.revenue.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-tertiary">{c.updated_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
